import React, { useState, useRef, useEffect } from "react";
import { Search, Plus } from "lucide-react";
import { showError, showSuccess } from "@/utils/toast";

interface Product {
    id: number;
    name: string;
    category_id: number;
    category: string;
    brand_id: number | null;
    brand_name: string | null;
    ean_code: string | null;
    base_shop_id?: number | null;
    base_shop_name?: string | null;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
    deposit_id?: number | null;
    deposit_type?: string | null;
    deposit_price?: number | null;
    deposit_currency?: string | null;
}

interface Price {
    id: string;
    type: string;
    typeId?: number | string;
    amount: string | number;
    currency: string;
    validFrom: string;
    validTo: string;
}

interface Packaging {
    id: string;
    value: string | number;
    unit: string;
    unitId?: number;
    minQuantity?: number;
}

interface DatabaseDeposit {
    id: number;
    deposit_type_id: number;
    deposit_type_name: string;
    deposit_type_code: string;
    price: number;
    country_id: number;
    currency_id: number;
    currency_code: string;
    currency_symbol: string;
}

interface DepositData {
    id: string;
    depositId: number;
    depositTypeId: number;
    depositTypeName: string;
    price: number;
    currency: string;
}

interface GroupAnnotatorMultiPriceFetcherProps {
    current?: { id?: number } | null;
    currentImageId?: number;
    currentSliceId?: number;
    onClosePanel?: () => void;
    onSave?: () => void | Promise<void>;
}

// STYLES (identical to GroupAnnotatorPriceFirstProduct)
const boxStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 200,
    borderRadius: 6,
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.04)",
    position: "relative",
    padding: "12px",
    boxSizing: "border-box",
    gap: "8px",
    overflow: "auto",
};

const labelStyle: React.CSSProperties = {
    color: "#ddd",
    fontSize: 13,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.5
};

const inputContainerStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
};

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    color: "#fff",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
};

const searchIconStyle: React.CSSProperties = {
    position: "absolute",
    right: 8,
    color: "#666",
    width: 16,
    height: 16,
};

const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#1a1a1a",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    maxHeight: 200,
    overflowY: "auto",
    zIndex: 100,
    marginTop: 2,
};

const dropdownItemStyle: React.CSSProperties = {
    padding: "8px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
    fontSize: 14,
    color: "#ddd",
    transition: "background-color 0.2s",
};

const messageStyle: React.CSSProperties = {
    padding: "8px 12px",
    backgroundColor: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    fontSize: 13,
    color: "#999",
    textAlign: "center",
};

const priceInputStyle: React.CSSProperties = {
    padding: "8px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "4px",
    color: "#ddd",
    fontSize: "14px",
    outline: "none",
} as React.CSSProperties & {
    "&::-webkit-outer-spin-button"?: React.CSSProperties;
    "&::-webkit-inner-spin-button"?: React.CSSProperties;
};

const sectionStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
};

const sectionHeaderStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#999",
    fontSize: "14px",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
};

const addButtonStyle: React.CSSProperties = {
    fontSize: "13px",
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    color: "#22c55e",
    border: "none",
    borderRadius: 4,
    padding: "4px 8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    transition: "all 0.2s",
};

const submitButtonStyle: React.CSSProperties = {
    padding: "10px 16px",
    backgroundColor: "rgba(34, 197, 94, 0.3)",
    border: "1px solid rgba(34, 197, 94, 0.3)",
    borderRadius: 4,
    color: "#22c55e",
    cursor: "pointer",
    fontSize: 15,
    fontWeight: "bold",
    transition: "all 0.2s",
};

const priceRowStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "6px",
    position: "relative",
};

const packagingRowStyle: React.CSSProperties = {
    display: "flex",
    gap: "8px",
    alignItems: "flex-end",
    padding: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "6px",
    position: "relative",
};

const deleteBtnStyle: React.CSSProperties = {
    position: "absolute",
    top: "4px",
    right: "4px",
    width: "20px",
    height: "20px",
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
    opacity: 0,
    transition: "opacity 0.2s",
};

const DEFAULT_PRICE_TYPES = ['Eredeti ár', 'Akciós ár', '2:1 ajánlat', 'Törzsvásárlói ár', 'App ár'];
const CURRENCIES = ['€', 'Ft', '$', '£'];
const UNITS = ['db', 'ml', 'g', 'kg', 'l', 'cl'];

const SearchableSelect: React.FC<{
    label?: string;
    options: string[];
    value: string;
    onChange: (val: string) => void;
    onAddNew?: (val: string) => void;
    placeholder?: string;
    style?: React.CSSProperties;
}> = ({ label, options, value, onChange, onAddNew, placeholder, style }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filtered = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));
    const exactMatch = options.find(opt => opt.toLowerCase() === search.toLowerCase());

    return (
        <div style={{ position: "relative", flex: 1, ...style }}>
            {label && (
                <label style={{ fontSize: "11px", color: "#999", fontWeight: "bold", textTransform: "uppercase", display: "block", marginBottom: "4px" }}>
                    {label}
                </label>
            )}
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    style={{
                        ...priceInputStyle,
                        width: "100%",
                    }}
                    placeholder={placeholder || 'Keresés...'}
                    value={isOpen ? search : value}
                    onFocus={() => { setIsOpen(true); setSearch(''); }}
                    onBlur={() => setTimeout(() => setIsOpen(false), 200)}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setIsOpen(false);
                            setSearch("");
                        }
                    }}
                />
                {isOpen && (
                    <div style={{
                        position: "absolute",
                        zIndex: 50,
                        width: "100%",
                        marginTop: "4px",
                        backgroundColor: "#0a0a0a",
                        border: "1px solid rgba(34, 197, 94, 0.3)",
                        borderRadius: "6px",
                        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.6)",
                        maxHeight: "200px",
                        overflow: "auto",
                        padding: "4px",
                    }}>
                        {filtered.map(opt => (
                            <button
                                key={opt}
                                onMouseDown={() => { onChange(opt); setIsOpen(false); }}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "6px 8px",
                                    fontSize: "13px",
                                    backgroundColor: "transparent",
                                    border: "none",
                                    color: "#bbb",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.2)";
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
                                }}
                            >
                                {opt}
                            </button>
                        ))}
                        {!exactMatch && search && onAddNew && (
                            <button
                                onMouseDown={() => { onAddNew(search); setIsOpen(false); }}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "6px 8px",
                                    fontSize: "13px",
                                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                                    border: "1px solid rgba(34, 197, 94, 0.2)",
                                    borderRadius: "4px",
                                    color: "#22c55e",
                                    cursor: "pointer",
                                    marginTop: "4px",
                                    fontWeight: "bold",
                                }}
                            >
                                + Új: "{search}"
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function GroupAnnotatorMultiPriceFetcher({
    currentImageId,
    currentSliceId,
    onClosePanel,
    onSave,
}: GroupAnnotatorMultiPriceFetcherProps) {
    // TERMÉK KERESÉS - többszörös kiválasztás
    const [productQuery, setProductQuery] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    // ÁR & KISZERELÉS ADATOK (közös összes termékre)
    const [prices, setPrices] = useState<Price[]>([{
        id: Math.random().toString(36).substr(2, 9),
        type: DEFAULT_PRICE_TYPES[0],
        typeId: undefined,
        amount: '',
        currency: '€',
        validFrom: '',
        validTo: ''
    }]);
    const [packaging, setPackaging] = useState<Packaging[]>([{
        id: Math.random().toString(36).substr(2, 9),
        value: '',
        unit: UNITS[0],
        unitId: undefined,
        minQuantity: 1
    }]);
    const [deposits, setDeposits] = useState<DepositData[]>([]);
    const [allDatabaseDeposits, setAllDatabaseDeposits] = useState<DatabaseDeposit[]>([]);
    const [selectedAttributes, setSelectedAttributes] = useState<any[]>([]);
    const [priceTypes, setPriceTypes] = useState<any[]>([]);
    const [priceTypeNames, setPriceTypeNames] = useState<string[]>([]);
    const [currencies, setCurrencies] = useState<string[]>([]);
    const [packagingUnits, setPackagingUnits] = useState<any[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState<"product_search" | "pricing">("product_search");
    const [hiddenProductIds, setHiddenProductIds] = useState<Set<number>>(new Set());

    const productInputRef = useRef<HTMLInputElement>(null);

    // Load price types, units, attributes
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [priceTypesRes, unitsRes, depositsRes] = await Promise.all([
                    fetch("/api/admin/product_identifiers/price_types"),
                    fetch("/api/admin/units"),
                    fetch("/api/admin/deposits"),
                ]);

                const priceTypesData = await priceTypesRes.json();
                const unitsData = await unitsRes.json();
                const depositsData = await depositsRes.json();

                const types = priceTypesData.price_types || [];
                const validTypes = types.length > 0 ? types : DEFAULT_PRICE_TYPES.map((name, idx) => ({ id: idx + 1, name }));
                setPriceTypes(validTypes);
                const typeNames = validTypes.map((t: any) => t.name || t);
                setPriceTypeNames(typeNames);

                const unitsArray = Array.isArray(unitsData) ? unitsData : (unitsData.units || []);
                setPackagingUnits(unitsArray);

                // Load database deposits with their prices
                const allDeposits = Array.isArray(depositsData) ? depositsData : (depositsData?.deposits || []);
                setAllDatabaseDeposits(allDeposits);

                // Load deposit types from database
                // Load deposit types from database (not needed separately since we use allDatabaseDeposits)
                // const depositTypesArray = depositsData?.deposit_types || [];
                // setDepositTypes(depositTypesArray);

                setCurrencies(CURRENCIES);
            } catch (error) {
                console.error("Error fetching data:", error);
                setPriceTypes(DEFAULT_PRICE_TYPES);
                setCurrencies(CURRENCIES);
            }
        };

        fetchData();
    }, []);

    // Product search - keresés terméknéven és márkán
    useEffect(() => {
        if (productQuery.length < 2) {
            setProducts([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                const response = await fetch(
                    `/api/admin/price_first_product_identifiers/search?q=${encodeURIComponent(productQuery)}`
                );
                if (response.ok) {
                    const data = await response.json();
                    setProducts(data.products || []);
                    setShowProductDropdown(true);
                }
            } catch (error) {
                console.error("Error:", error);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [productQuery]);

    const handleProductSelect = (product: Product) => {
        // Ha már kiválasztottuk, akkor eltávolítjuk
        if (selectedProducts.find(p => p.id === product.id)) {
            setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
        } else {
            // Hozzáadunk
            setSelectedProducts(prev => [...prev, product]);
        }
        // Dropdown bezárása és input törlése kiválasztás után
        setShowProductDropdown(false);
        setProductQuery("");
        // Fókusz az input mezőre az újabb kereséshez
        setTimeout(() => productInputRef.current?.focus(), 0);
    };

    const handleSkipProduct = (productId: number) => {
        setHiddenProductIds(prev => new Set([...prev, productId]));
    };

    const handleRemoveProductFromList = (productId: number) => {
        setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    };

    const addPrice = () => {
        const usedTypes = prices.map(p => p.type);
        let nextType: string;
        let nextTypeId: number | undefined;

        if (priceTypes.length > 0) {
            const typeNames = priceTypes.map((t: any) => typeof t === 'object' ? t.name : t);
            nextType = typeNames.find((name: string) => !usedTypes.includes(name)) || typeNames[0];
            const selectedTypeObj = priceTypes.find((t: any) => (typeof t === 'object' ? t.name : t) === nextType);
            if (selectedTypeObj && typeof selectedTypeObj === 'object') {
                nextTypeId = selectedTypeObj.id;
            }
        } else {
            nextType = DEFAULT_PRICE_TYPES.find(t => !usedTypes.includes(t)) || DEFAULT_PRICE_TYPES[0];
        }

        setPrices([{
            id: Math.random().toString(36).substr(2, 9),
            type: nextType,
            typeId: nextTypeId,
            amount: '',
            currency: currencies.length > 0 ? currencies[0] : '€',
            validFrom: '',
            validTo: ''
        }, ...prices]);
    };

    const addPackaging = () => {
        if (packaging.length > 0) {
            const lastPackaging = packaging[packaging.length - 1];
            if (!lastPackaging.value || String(lastPackaging.value).trim() === '') {
                showError("Kérjük töltse ki az aktuális kiszerelést!");
                return;
            }
        }

        let defaultUnit = UNITS[0];
        let defaultUnitId: number | undefined;

        if (packagingUnits.length > 0) {
            const unit = packagingUnits[0];
            defaultUnit = unit.abbreviation || unit.name;
            defaultUnitId = unit.id;
        }

        setPackaging([...packaging, {
            id: Math.random().toString(36).substr(2, 9),
            value: '',
            unit: defaultUnit,
            unitId: defaultUnitId,
            minQuantity: 1
        }]);
    };

    const deletePrice = (id: string) => {
        setPrices(prev => prev.filter(p => p.id !== id));
    };

    const deletePackaging = (id: string) => {
        setPackaging(prev => prev.filter(p => p.id !== id));
    };

    const updatePrice = (id: string, field: keyof Price, value: any) => {
        setPrices(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    };

    const updatePackaging = (id: string, field: keyof Packaging, value: any) => {
        setPackaging(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    };

    const addDeposit = () => {
        if (allDatabaseDeposits.length === 0) {
            showError("Nincsenek elérhető betétdíjak az adatbázisban!");
            return;
        }

        // Check if all existing deposits are already selected
        const selectedTypeIds = deposits.map(d => d.depositTypeId);
        const availableDeposits = allDatabaseDeposits.filter(d => !selectedTypeIds.includes(d.deposit_type_id));

        if (availableDeposits.length === 0) {
            showError("Az összes betétdíj már hozzáadva!");
            return;
        }

        const firstAvailable = availableDeposits[0];
        setDeposits([...deposits, {
            id: Math.random().toString(36).substr(2, 9),
            depositId: firstAvailable.id,
            depositTypeId: firstAvailable.deposit_type_id,
            depositTypeName: firstAvailable.deposit_type_name,
            price: firstAvailable.price,
            currency: firstAvailable.currency_symbol
        }]);
    };

    const deleteDeposit = (id: string) => {
        setDeposits(prev => prev.filter(d => d.id !== id));
    };

    const updateDeposit = (id: string, field: keyof DepositData, value: any) => {
        // If changing depositTypeId, update price automatically from database
        if (field === 'depositTypeId') {
            const selectedDeposit = allDatabaseDeposits.find(d => d.deposit_type_id === value);
            if (selectedDeposit) {
                setDeposits(prev => prev.map(d => d.id === id ? {
                    ...d,
                    depositTypeId: selectedDeposit.deposit_type_id,
                    depositTypeName: selectedDeposit.deposit_type_name,
                    depositId: selectedDeposit.id,
                    price: selectedDeposit.price,
                    currency: selectedDeposit.currency_symbol
                } : d));
            }
        } else {
            setDeposits(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
        }
    };

    const handleSaveOffers = async () => {
        if (selectedProducts.length === 0) {
            showError("Válasszon ki legalább egy terméket!");
            return;
        }

        if (prices.length === 0 && packaging.length === 0) {
            showError("Legalább egy árat vagy kiszerelést adjon meg!");
            return;
        }

        if (!currentSliceId || !currentImageId) {
            showError("Hiányzik a slice vagy image adat!");
            return;
        }

        setSubmitting(true);
        try {
            const getPackagingUnitId = (pack: Packaging): number | null => {
                if (pack.unitId) {
                    return pack.unitId;
                }
                const foundUnit = packagingUnits.find(u =>
                    (u.abbreviation === pack.unit || u.name === pack.unit)
                );
                return foundUnit?.id || null;
            };

            // Multi API hívás összes termékre
            for (const product of selectedProducts) {
                // Process first packaging or prices
                if (packaging.length > 0) {
                    const firstPack = packaging[0];
                    const packagingUnitId = getPackagingUnitId(firstPack);
                    const packagingSize = firstPack.value.toString();

                    const payload = {
                        product_id: product.id,
                        packaging_size: packagingSize || null,
                        packaging_unit_id: packagingUnitId,
                        packaging_multiplier: "1",
                        attribute_ids: selectedAttributes.map(a => a.id),
                        new_attributes: selectedAttributes
                            .filter(a => a.isNew)
                            .map(a => ({ name: a.name, attribute_type: a.attribute_type })),
                        min_quantity: firstPack.minQuantity || 1,
                        image_id: currentImageId,
                        slice_id: currentSliceId
                    };

                    const response = await fetch("/api/admin/product_identifiers/multi", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    });

                    const data = await response.json();
                    if (!data.success || !data.offer) {
                        showError(data.error || `Hiba az ajánlat létrehozásakor (${product.name})!`);
                        continue;
                    }

                    // Add prices
                    for (const price of prices) {
                        const priceAmount = parseFloat(String(price.amount)) || 0;
                        if (priceAmount > 0) {
                            const selectedType = priceTypes.find((t: any) => t.name === price.type);
                            const typeId = price.typeId || selectedType?.id;

                            if (typeId) {
                                await fetch("/api/admin/product_identifiers/add_price", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                        image_offer_id: data.offer.id,
                                        price_type_id: typeId,
                                        value: priceAmount,
                                        starts_at: price.validFrom || null,
                                        ends_at: price.validTo || null,
                                    }),
                                });
                            }
                        }
                    }

                    // Add deposits
                    for (const deposit of deposits) {
                        if (deposit.depositId && deposit.price > 0) {
                            await fetch("/api/admin/product_identifiers/add_deposit", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                    product_variant_id: data.variant.id,
                                    deposit_id: deposit.depositId,
                                }),
                            });
                        }
                    }

                    // Process additional packagings
                    for (let i = 1; i < packaging.length; i++) {
                        const pack = packaging[i];
                        const packagingUnitId = getPackagingUnitId(pack);
                        const packagingSize = pack.value.toString();

                        const payload = {
                            product_id: product.id,
                            packaging_size: packagingSize,
                            packaging_unit_id: packagingUnitId,
                            packaging_multiplier: "1",
                            attribute_ids: selectedAttributes.map(a => a.id),
                            new_attributes: selectedAttributes
                                .filter(a => a.isNew)
                                .map(a => ({ name: a.name, attribute_type: a.attribute_type })),
                            min_quantity: pack.minQuantity || 1,
                            image_id: currentImageId,
                            slice_id: currentSliceId
                        };

                        const response = await fetch("/api/admin/product_identifiers/multi", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(payload),
                        });

                        const data = await response.json();
                        if (data.success && data.offer) {
                            for (const price of prices) {
                                const priceAmount = parseFloat(String(price.amount)) || 0;
                                if (priceAmount > 0) {
                                    const selectedType = priceTypes.find((t: any) => t.name === price.type);
                                    const typeId = price.typeId || selectedType?.id;

                                    if (typeId) {
                                        await fetch("/api/admin/product_identifiers/add_price", {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({
                                                image_offer_id: data.offer.id,
                                                price_type_id: typeId,
                                                value: priceAmount,
                                                starts_at: price.validFrom || null,
                                                ends_at: price.validTo || null,
                                            }),
                                        });
                                    }
                                }
                            }

                            // Add deposits for additional packagings
                            for (const deposit of deposits) {
                                if (deposit.depositId && deposit.price > 0) {
                                    await fetch("/api/admin/product_identifiers/add_deposit", {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({
                                            product_variant_id: data.variant.id,
                                            deposit_id: deposit.depositId,
                                        }),
                                    });
                                }
                            }
                        }
                    }
                } else {
                    // No packaging, only prices
                    if (prices.length > 0) {
                        const payload = {
                            product_id: product.id,
                            packaging_size: null,
                            packaging_unit_id: null,
                            packaging_multiplier: "1",
                            attribute_ids: selectedAttributes.map(a => a.id),
                            new_attributes: selectedAttributes
                                .filter(a => a.isNew)
                                .map(a => ({ name: a.name, attribute_type: a.attribute_type })),
                            min_quantity: 1,
                            image_id: currentImageId,
                            slice_id: currentSliceId,
                        };

                        const response = await fetch("/api/admin/product_identifiers/multi", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(payload),
                        });

                        const data = await response.json();
                        if (!data.success || !data.offer) {
                            showError(data.error || `Hiba az ajánlat létrehozásakor (${product.name})!`);
                            continue;
                        }

                        // Add prices
                        for (const price of prices) {
                            const priceAmount = parseFloat(String(price.amount)) || 0;
                            if (priceAmount > 0) {
                                const selectedType = priceTypes.find((t: any) => t.name === price.type);
                                const typeId = price.typeId || selectedType?.id;

                                if (typeId) {
                                    await fetch("/api/admin/product_identifiers/add_price", {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({
                                            image_offer_id: data.offer.id,
                                            price_type_id: typeId,
                                            value: priceAmount,
                                            starts_at: price.validFrom || null,
                                            ends_at: price.validTo || null,
                                        }),
                                    });
                                }
                            }
                        }

                        // Add deposits
                        for (const deposit of deposits) {
                            if (deposit.depositId && deposit.price > 0) {
                                await fetch("/api/admin/product_identifiers/add_deposit", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                        product_variant_id: data.variant.id,
                                        deposit_id: deposit.depositId,
                                    }),
                                });
                            }
                        }
                    }
                }
            }

            setSelectedProducts([]);
            setProductQuery("");
            setPrices([]);
            setPackaging([]);
            setDeposits([]);
            setSelectedAttributes([]);
            setStep("product_search");
            showSuccess(`${selectedProducts.length} termék sikeresen felvéve!`);

            // Frissítjük az azonosított termékeket
            if (onSave) {
                await onSave();
            }
        } catch (error) {
            showError("Hiba az ajánlatok létrehozásakor!");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <style>{`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                input[type="number"] {
                    -moz-appearance: textfield;
                }
            `}</style>
            <div style={boxStyle}>
                <div style={labelStyle}>💸 Multi-Árfelvevő</div>

                {step === "product_search" ? (
                    <>
                        {/* LÉPÉS 1: Termék keresése (többszörös) */}
                        <div style={{ position: "relative" }}>
                            <div style={inputContainerStyle}>
                                <input
                                    ref={productInputRef}
                                    type="text"
                                    placeholder="Termék neve (pl. Coca-Cola)..."
                                    value={productQuery}
                                    onChange={(e) => setProductQuery(e.target.value)}
                                    onFocus={() => products.length > 0 && setShowProductDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                            setShowProductDropdown(false);
                                            setProductQuery("");
                                        }
                                    }}
                                    style={inputStyle}
                                />
                                <Search style={searchIconStyle} size={16} />
                            </div>

                            {showProductDropdown && products.length > 0 && (
                                <div style={dropdownStyle}>
                                    {products
                                        .filter(product => !hiddenProductIds.has(product.id))
                                        .map((product) => (
                                            <div
                                                key={product.id}
                                                style={{
                                                    ...dropdownItemStyle,
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    backgroundColor: selectedProducts.find(p => p.id === product.id)
                                                        ? "rgba(34, 197, 94, 0.2)"
                                                        : "transparent"
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!selectedProducts.find(p => p.id === product.id)) {
                                                        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = selectedProducts.find(p => p.id === product.id)
                                                        ? "rgba(34, 197, 94, 0.2)"
                                                        : "transparent";
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 3,
                                                        flex: 1,
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => handleProductSelect(product)}
                                                >
                                                    <div style={{ fontWeight: 500 }}>
                                                        {selectedProducts.find(p => p.id === product.id) ? "✓ " : ""}{product.name}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 11,
                                                        color: "#aaa",
                                                        display: "flex",
                                                        gap: 8,
                                                        flexWrap: "wrap"
                                                    }}>
                                                        {product.brand_name && (
                                                            <span style={{
                                                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                                                padding: "2px 6px",
                                                                borderRadius: "3px"
                                                            }}>
                                                                🏷️ {product.brand_name}
                                                            </span>
                                                        )}
                                                        <span>📂 {product.category}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSkipProduct(product.id);
                                                    }}
                                                    style={{
                                                        padding: "4px 8px",
                                                        backgroundColor: "rgba(239, 68, 68, 0.15)",
                                                        border: "1px solid rgba(239, 68, 68, 0.3)",
                                                        borderRadius: 3,
                                                        color: "#ef4444",
                                                        cursor: "pointer",
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        whiteSpace: "nowrap",
                                                        transition: "all 0.2s",
                                                        flexShrink: 0,
                                                    }}
                                                    title="Kihagyás"
                                                >
                                                    ⊘ Skip
                                                </button>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Kiválasztott termékek listája */}
                        {selectedProducts.length > 0 && (
                            <div style={{
                                backgroundColor: "rgba(34, 197, 94, 0.1)",
                                border: "1px solid rgba(34, 197, 94, 0.3)",
                                borderRadius: 6,
                                padding: "12px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px"
                            }}>
                                <div style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#22c55e"
                                }}>
                                    ✓ {selectedProducts.length} termék kiválasztva
                                </div>
                                {selectedProducts.map(product => (
                                    <div
                                        key={product.id}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px 12px",
                                            backgroundColor: "rgba(34, 197, 94, 0.05)",
                                            borderRadius: 4,
                                            fontSize: 13,
                                            color: "#ddd"
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{product.name}</div>
                                            <div style={{ fontSize: 11, color: "#aaa" }}>
                                                {product.brand_name && `${product.brand_name} - `}
                                                {product.category}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveProductFromList(product.id)}
                                            style={{
                                                padding: "4px 8px",
                                                backgroundColor: "rgba(239, 68, 68, 0.2)",
                                                border: "1px solid rgba(239, 68, 68, 0.3)",
                                                borderRadius: 3,
                                                color: "#ef4444",
                                                cursor: "pointer",
                                                fontSize: 12,
                                                fontWeight: 500,
                                                transition: "all 0.2s",
                                            }}
                                            onMouseEnter={(e) => {
                                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(239, 68, 68, 0.4)";
                                            }}
                                            onMouseLeave={(e) => {
                                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(239, 68, 68, 0.2)";
                                            }}
                                        >
                                            Eltávolít
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedProducts.length > 0 && (
                            <button
                                onClick={() => setStep("pricing")}
                                style={{
                                    ...submitButtonStyle,
                                    backgroundColor: "rgba(34, 197, 94, 0.3)",
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.5)";
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.3)";
                                }}
                            >
                                ➜ Tovább a közös árfelvételhez
                            </button>
                        )}
                    </>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", height: "100%", overflow: "auto" }}>
                        <div style={messageStyle}>
                            ✅ {selectedProducts.length} termék kiválasztva - közös árfelvétel
                        </div>

                        {/* ÁRAK */}
                        <div style={sectionStyle}>
                            <div style={sectionHeaderStyle}>
                                <span>Árak (mindegyikre)</span>
                                <button
                                    onClick={addPrice}
                                    style={addButtonStyle}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.2)";
                                    }}
                                >
                                    <Plus size={12} /> Ár
                                </button>
                            </div>

                            {prices.length === 0 ? (
                                <div style={{ textAlign: "center", color: "#666", fontSize: "13px", padding: "12px" }}>
                                    Nincs ár felvéve
                                </div>
                            ) : (
                                prices.map((price) => (
                                    <div
                                        key={price.id}
                                        style={priceRowStyle}
                                        onMouseEnter={(e) => {
                                            const btn = e.currentTarget.querySelector('button') as HTMLButtonElement;
                                            if (btn) btn.style.opacity = "1";
                                        }}
                                        onMouseLeave={(e) => {
                                            const btn = e.currentTarget.querySelector('button') as HTMLButtonElement;
                                            if (btn) btn.style.opacity = "0";
                                        }}
                                    >
                                        <button
                                            onClick={() => deletePrice(price.id)}
                                            style={deleteBtnStyle}
                                        >
                                            ✕
                                        </button>

                                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                                            <div style={{ flex: 1 }}>
                                                <SearchableSelect
                                                    options={priceTypeNames.filter((name: string) => !prices.map(p => p.type).includes(name) || name === price.type)}
                                                    value={price.type}
                                                    onChange={(val) => {
                                                        const selectedType = priceTypes.find((t: any) => t.name === val);
                                                        updatePrice(price.id, 'type', val);
                                                        if (selectedType) {
                                                            updatePrice(price.id, 'typeId', selectedType.id);
                                                        }
                                                    }}
                                                    placeholder="Ár típus"
                                                />
                                            </div>

                                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                    value={price.amount}
                                                    onChange={(e) => updatePrice(price.id, 'amount', e.target.value)}
                                                    style={{
                                                        ...priceInputStyle,
                                                        width: "80px",
                                                    }}
                                                />
                                                <select
                                                    value={price.currency}
                                                    onChange={(e) => updatePrice(price.id, 'currency', e.target.value)}
                                                    style={{
                                                        ...priceInputStyle,
                                                        width: "60px",
                                                    }}
                                                >
                                                    {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", gap: "4px", alignItems: "center", fontSize: "9px" }}>
                                            <input
                                                type="date"
                                                value={price.validFrom}
                                                onChange={(e) => updatePrice(price.id, 'validFrom', e.target.value)}
                                                style={{
                                                    ...priceInputStyle,
                                                    flex: 1,
                                                }}
                                            />
                                            <input
                                                type="date"
                                                value={price.validTo}
                                                onChange={(e) => updatePrice(price.id, 'validTo', e.target.value)}
                                                style={{
                                                    ...priceInputStyle,
                                                    flex: 1,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* KISZERELÉS */}
                        <div style={sectionStyle} data-packaging-section>
                            <div style={sectionHeaderStyle}>
                                <span>Kiszerelés (mindegyikre)</span>
                                <button
                                    onClick={() => {
                                        addPackaging();
                                        setTimeout(() => {
                                            const container = document.querySelector('[data-packaging-section]');
                                            if (container) {
                                                container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                            }
                                        }, 50);
                                    }}
                                    style={addButtonStyle}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.2)";
                                    }}
                                >
                                    <Plus size={12} /> Kiszerelés
                                </button>
                            </div>

                            {packaging.length === 0 ? (
                                <div style={{ textAlign: "center", color: "#666", fontSize: "13px", padding: "12px" }}>
                                    Nincs kiszerelés felvéve
                                </div>
                            ) : (
                                packaging.map((pack) => (
                                    <div
                                        key={pack.id}
                                        style={packagingRowStyle}
                                        onMouseEnter={(e) => {
                                            const btn = e.currentTarget.querySelector('button') as HTMLButtonElement;
                                            if (btn) btn.style.opacity = "1";
                                        }}
                                        onMouseLeave={(e) => {
                                            const btn = e.currentTarget.querySelector('button') as HTMLButtonElement;
                                            if (btn) btn.style.opacity = "0";
                                        }}
                                    >
                                        <button
                                            onClick={() => deletePackaging(pack.id)}
                                            style={deleteBtnStyle}
                                        >
                                            ✕
                                        </button>

                                        <input
                                            type="text"
                                            placeholder="Érték"
                                            value={pack.value}
                                            onChange={(e) => updatePackaging(pack.id, 'value', e.target.value)}
                                            style={{
                                                ...priceInputStyle,
                                                flex: 1,
                                                minWidth: "50px",
                                            }}
                                        />

                                        <select
                                            value={pack.unit}
                                            onChange={(e) => {
                                                const newUnit = e.target.value;
                                                const foundUnit = packagingUnits.find(u =>
                                                    (u.abbreviation === newUnit || u.name === newUnit)
                                                );
                                                if (foundUnit) {
                                                    const updated = [...packaging];
                                                    const idx = updated.findIndex(p => p.id === pack.id);
                                                    if (idx >= 0) {
                                                        updated[idx].unit = newUnit;
                                                        updated[idx].unitId = foundUnit.id;
                                                        setPackaging(updated);
                                                    }
                                                } else {
                                                    updatePackaging(pack.id, 'unit', newUnit);
                                                }
                                            }}
                                            style={{
                                                ...priceInputStyle,
                                                minWidth: "50px",
                                            }}
                                        >
                                            {packagingUnits.length > 0 ? (
                                                packagingUnits.map((unit: any) => (
                                                    <option key={unit.id || unit.abbreviation} value={unit.abbreviation || unit.name}>
                                                        {unit.abbreviation || unit.name}
                                                    </option>
                                                ))
                                            ) : (
                                                UNITS.map(u => <option key={u} value={u}>{u}</option>)
                                            )}
                                        </select>

                                        <input
                                            type="number"
                                            placeholder="Min. (1)"
                                            value={pack.minQuantity !== undefined && pack.minQuantity !== null ? pack.minQuantity : ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '') {
                                                    updatePackaging(pack.id, 'minQuantity', undefined);
                                                } else {
                                                    const num = Number(val);
                                                    if (!isNaN(num) && num >= 0) {
                                                        updatePackaging(pack.id, 'minQuantity', num);
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '' || Number(e.target.value) < 1) {
                                                    updatePackaging(pack.id, 'minQuantity', 1);
                                                }
                                            }}
                                            style={{
                                                ...priceInputStyle,
                                                minWidth: "60px",
                                            }}
                                        />
                                    </div>
                                ))
                            )}
                        </div>

                        {/* BETÉTDÍJAK */}
                        <div style={sectionStyle} data-deposit-section>
                            <div style={sectionHeaderStyle}>
                                <span>💰 Betétdíjak (mindegyikre)</span>
                                <button
                                    onClick={() => {
                                        addDeposit();
                                        setTimeout(() => {
                                            const container = document.querySelector('[data-deposit-section]');
                                            if (container) {
                                                container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                            }
                                        }, 50);
                                    }}
                                    style={addButtonStyle}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.2)";
                                    }}
                                >
                                    <Plus size={12} /> Betétdíj
                                </button>
                            </div>

                            {deposits.length === 0 ? (
                                <div style={{ textAlign: "center", color: "#666", fontSize: "13px", padding: "12px" }}>
                                    Nincs betétdíj felvéve
                                </div>
                            ) : (
                                deposits.map((deposit) => (
                                    <div
                                        key={deposit.id}
                                        style={priceRowStyle}
                                        onMouseEnter={(e) => {
                                            const btn = e.currentTarget.querySelector('button') as HTMLButtonElement;
                                            if (btn) btn.style.opacity = "1";
                                        }}
                                        onMouseLeave={(e) => {
                                            const btn = e.currentTarget.querySelector('button') as HTMLButtonElement;
                                            if (btn) btn.style.opacity = "0";
                                        }}
                                    >
                                        <button
                                            onClick={() => deleteDeposit(deposit.id)}
                                            style={deleteBtnStyle}
                                        >
                                            ✕
                                        </button>

                                        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={labelStyle}>Betétdíj Típus</label>
                                                <select
                                                    value={deposit.depositTypeId || ''}
                                                    onChange={(e) => {
                                                        const selectedId = parseInt(e.target.value);
                                                        updateDeposit(deposit.id, 'depositTypeId', selectedId);
                                                    }}
                                                    style={{
                                                        ...priceInputStyle,
                                                        width: "100%",
                                                    }}
                                                >
                                                    <option value="">-- Válassz --</option>
                                                    {allDatabaseDeposits.map(dbDeposit => (
                                                        <option key={dbDeposit.id} value={dbDeposit.deposit_type_id}>
                                                            {dbDeposit.deposit_type_name} ({dbDeposit.deposit_type_code})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div style={{ display: "flex", gap: "4px", alignItems: "flex-end" }}>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                    <label style={{ ...labelStyle, fontSize: "11px" }}>Ár</label>
                                                    <input
                                                        type="text"
                                                        disabled
                                                        placeholder="0.00"
                                                        value={deposit.price}
                                                        style={{
                                                            ...priceInputStyle,
                                                            width: "80px",
                                                            backgroundColor: "rgba(255,255,255,0.02)",
                                                            cursor: "not-allowed",
                                                            opacity: 0.6,
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                    <label style={{ ...labelStyle, fontSize: "11px" }}>Pénznem</label>
                                                    <input
                                                        type="text"
                                                        disabled
                                                        value={deposit.currency}
                                                        style={{
                                                            ...priceInputStyle,
                                                            width: "60px",
                                                            backgroundColor: "rgba(255,255,255,0.02)",
                                                            cursor: "not-allowed",
                                                            opacity: 0.6,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* FELVÉTEL GOMB */}
                        <button
                            onClick={handleSaveOffers}
                            disabled={submitting}
                            style={{
                                ...submitButtonStyle,
                                opacity: submitting ? 0.5 : 1,
                                cursor: submitting ? "not-allowed" : "pointer"
                            }}
                            onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.5)";
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(34, 197, 94, 0.3)";
                            }}
                        >
                            📤 {submitting ? "Mentés..." : `Összes (${selectedProducts.length}) termék felvétele`}
                        </button>

                        {/* VISSZA GOMB */}
                        <button
                            onClick={() => {
                                setSelectedProducts([]);
                                setPrices([{
                                    id: Math.random().toString(36).substr(2, 9),
                                    type: DEFAULT_PRICE_TYPES[0],
                                    typeId: undefined,
                                    amount: '',
                                    currency: '€',
                                    validFrom: '',
                                    validTo: ''
                                }]);
                                setPackaging([{
                                    id: Math.random().toString(36).substr(2, 9),
                                    value: '',
                                    unit: UNITS[0],
                                    unitId: undefined,
                                    minQuantity: 1
                                }]);
                                setDeposits([]);
                                setSelectedAttributes([]);
                                setStep("product_search");
                                setHiddenProductIds(new Set());
                                onClosePanel?.();
                            }}
                            disabled={submitting}
                            style={{
                                ...submitButtonStyle,
                                backgroundColor: "rgba(239, 68, 68, 0.2)",
                                borderColor: "rgba(239, 68, 68, 0.3)",
                                color: "#ef4444",
                                opacity: submitting ? 0.5 : 1,
                                cursor: submitting ? "not-allowed" : "pointer"
                            }}
                            onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(239, 68, 68, 0.4)";
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(239, 68, 68, 0.2)";
                            }}
                        >
                            ← Vissza
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
